/**
 * Production Config
 * Copyright 2025 ENERCALC, LLC
 * All rights reserved.
 *
 * This file is used to configure the application for production.
 *
 * @author: ENERCALC, LLC
 * @version: 1.0.0
 */

// Re-export awsRegions and its RegionInfo type so callers can import from one place.
export { awsRegions } from "./aws-regions.js";
export type { RegionInfo } from "./aws-regions.js";

/**
 * Sentry DSN, shared by both the production (`app.ts`) and test (`test-app.ts`)
 * entry points. Only the DSN is shared — each entry's `Sentry.init` otherwise
 * differs (production carries the full integration options; the test site sets
 * `environment: "test"`).
 */
export const SENTRY_DSN =
  "https://c372ce87842a2c9d87d7ae05a6396094@o4509011001344000.ingest.us.sentry.io/4509555974733824";

/**
 * AWS region name constants keyed by short region label.
 * Use `as const` so TypeScript infers narrow string-literal value types for each key.
 */
export const AwsRegionNames = {
  NORCAL: "us-west-1",
  NOVA: "us-east-1",
  OHIO: "us-east-2",
  MUMBAI: "ap-south-1",
  TOKYO: "ap-northeast-1",
  FRANKFURT: "eu-central-1",
  OREGON: "us-west-2",
} as const;

/** Union of all region label keys (e.g. "NORCAL" | "NOVA" | ...). */
export type AwsRegionKey = keyof typeof AwsRegionNames;

/** Union of all AWS region ID values (e.g. "us-west-1" | "us-east-1" | ...). */
export type AwsRegionId = (typeof AwsRegionNames)[AwsRegionKey];

/**
 * Frame account configuration for a single region.
 * Both IDs are UUIDs assigned by the Frame control plane.
 */
export interface FrameAccountConfig {
  readonly terminalConfigId: string;
  readonly applicationId: string;
}

/**
 * Frame account configs keyed by AwsRegionKey.
 * Using `Record<string, FrameAccountConfig>` allows string-key indexing (e.g.
 * `FrameAccountConfigs[account]` where `account` is `string | null`).
 */
export const FrameAccountConfigs: Readonly<Record<string, FrameAccountConfig>> = {
  NORCAL: {
    terminalConfigId: "b19d1f58-257d-4773-ae7c-f6aa3bcc9f2f.25794e94-8068-424e-ae10-0cb1a3f74f16",
    applicationId: "563c4293-4ebc-48e2-b8da-be4b5ed0942b",
  },
  NOVA: {
    terminalConfigId: "45404186-64c1-4bb3-b123-3572a7dc159c.58e4f99f-5b4b-4c88-aafa-82958d7d2922",
    applicationId: "774a02c1-9061-4dad-9553-1d0d6a07baf9",
  },
  OHIO: {
    terminalConfigId: "692c1447-05c1-4d58-ae13-24e56da4d1e8.94dd52c0-0d03-46f6-8ed1-64a08654396a",
    applicationId: "f9e7d4f8-d469-41e2-8b45-489fb2e5ffd2",
  },
  MUMBAI: {
    terminalConfigId: "c894c8bf-7420-4a9d-ae8d-67a250492a1c.77c32ea4-964f-4a1e-bf5c-f88106fb60e7",
    applicationId: "e73480e7-3fff-4142-a1d3-e629030506e3",
  },
  TOKYO: {
    terminalConfigId: "b83e9954-0af8-463b-a600-57d1cf7edc2d.1522014a-ec11-4ce9-992d-203f35ca5e4d",
    applicationId: "df43d950-6d2f-4a5e-ad9e-ccdd799a19fd",
  },
  FRANKFURT: {
    terminalConfigId: "a780751e-fc34-4775-bb34-1e0cd7628e2f.91bdeadd-4225-48d3-9bf5-d8ca03c97877",
    applicationId: "f862a2d9-9ce9-49a1-a769-2171471df19d",
  },
  OREGON: {
    terminalConfigId: "d0c49975-4ec8-4896-a08d-0fe19921c49a.0b803c02-a7c2-44ea-b82d-5180ff409693",
    applicationId: "0184bbb0-dbe8-4cf8-af4e-0e3db4b372b9",
  },
};

/**
 * Cookie name constants for persisting user credentials across sessions.
 * 180-day cookie max-age is set in setCookie() callers.
 */
export const CookieNames = {
  KWNUMBER: "enercalc_kw_number_cloud_launch",
  USERNAME: "enercalc_user_name_cloud_launch",
  PASSWORD: "enercalc_password_cloud_launch",
} as const;

/** Keys of the CookieNames constant. */
export type CookieNameKey = keyof typeof CookieNames;

/**
 * API endpoint URLs that the application posts to.
 * All endpoints are frozen via `as const` — no runtime mutation permitted.
 */
export const ApiEndpoints = {
  TOKEN: "https://eneractivtest.com/api/v2/frame/token",
  LOGIN: "https://eneractivtest.com/api/v1/utility/cloud/login/validate",
  FRAME_GQL: "https://api.use.difr.com/api/graphql",
} as const;

/** Keys of the ApiEndpoints constant. */
export type ApiEndpointKey = keyof typeof ApiEndpoints;

/**
 * localStorage key names used by the application.
 * Matches AGENTS.md: `frame-token` and `aws-notification-dismissed`.
 */
export const StorageKeys = {
  FRAME_TOKEN: "frame-token",
  NOTIFICATION_DISMISSED: "aws-notification-dismissed",
} as const;

/** Keys of the StorageKeys constant. */
export type StorageKey = keyof typeof StorageKeys;
